import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { company } from "../data/company";
import { serviceCategories, servicesGrouped } from "../data/services";
import { cities } from "../data/cities";
import { trackClick } from "../utils/analytics";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [submenu, setSubmenu] = useState(null); // 'diensten' | 'locaties' | null
  const closeTimer = React.useRef(null);
  const loc = useLocation();
  const isModern = true;

  const openSubmenu = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setSubmenu(name);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setSubmenu(null), 220);
  };
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  React.useEffect(() => {
    setOpen(false);
    setSubmenu(null);
  }, [loc.pathname]);

  // Modern (light) theme classes for homepage
  const headerCls = isModern
    ? "fixed top-0 left-0 right-0 z-50 bg-white/92 backdrop-blur-xl border-b border-m-border-soft"
    : "fixed top-0 left-0 right-0 z-50 bg-koning-bg/85 backdrop-blur-xl border-b border-koning-border";
  const linkBase = isModern
    ? "text-[14px] font-semibold tracking-normal whitespace-nowrap transition-colors"
    : "text-[12px] tracking-[0.16em] uppercase whitespace-nowrap transition-colors";
  const linkColor = (active) =>
    isModern
      ? (active ? "text-m-orange" : "text-m-text hover:text-m-orange")
      : (active ? "text-koning-gold" : "text-white hover:text-koning-gold");
  const submenuPanelCls = isModern
    ? "hidden lg:block absolute left-0 right-0 top-full bg-white border-t border-m-border-soft shadow-soft-lg"
    : "hidden lg:block absolute left-0 right-0 top-full bg-koning-bg border-t border-koning-border";
  const phoneCls = isModern
    ? "flex items-center gap-2 text-m-text-2 hover:text-m-orange transition-colors whitespace-nowrap"
    : "flex items-center gap-2 text-koning-muted hover:text-koning-gold transition-colors whitespace-nowrap";

  return (
    <header
      className={headerCls}
      data-testid="main-navbar"
      style={isModern ? { backgroundColor: "rgba(255,255,255,0.92)" } : undefined}
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-[72px] md:h-[80px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="navbar-logo">
            <img
              src={isModern ? "/brand/logo.svg" : "/brand/logo-white.svg"}
              alt="Koning Aannemersbedrijf"
              className="h-12 md:h-14 lg:h-16 w-auto"
              onError={(e) => { e.target.src = "/brand/logo-white.svg"; }}
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
            <NavItem to="/" label="Home" onHover={scheduleClose} isModern={isModern} />
            <div onMouseEnter={() => openSubmenu("diensten")} onMouseLeave={scheduleClose} className="flex items-center">
              <NavLink
                to="/diensten"
                className={({ isActive }) =>
                  `flex items-center gap-1.5 ${linkBase} ${linkColor(isActive || submenu === "diensten")}`
                }
                data-testid="navbar-diensten-trigger"
              >
                Diensten <ChevronDown size={13} className={submenu === "diensten" ? "rotate-180 transition-transform" : "transition-transform"} />
              </NavLink>
            </div>
            <div onMouseEnter={() => openSubmenu("locaties")} onMouseLeave={scheduleClose} className="flex items-center">
              <NavLink
                to="/locaties"
                className={({ isActive }) =>
                  `flex items-center gap-1.5 ${linkBase} ${linkColor(isActive || submenu === "locaties")}`
                }
                data-testid="navbar-locaties-trigger"
              >
                Locaties <ChevronDown size={13} className={submenu === "locaties" ? "rotate-180 transition-transform" : "transition-transform"} />
              </NavLink>
            </div>
            <NavItem to="/projecten" label="Projecten" onHover={scheduleClose} isModern={isModern} />
            <NavItem to="/configurator" label="Configurator" onHover={scheduleClose} isModern={isModern} />
            <NavItem to="/over-ons" label="Over Ons" onHover={scheduleClose} isModern={isModern} />
            <NavItem to="/reviews" label="Reviews" onHover={scheduleClose} isModern={isModern} />
            <NavItem to="/contact" label="Contact" onHover={scheduleClose} isModern={isModern} />
          </nav>

          <div className="hidden md:flex items-center gap-4 shrink-0">
            <a href={company.phoneHref} onClick={() => trackClick("phone", "navbar")} className={phoneCls} data-testid="navbar-phone">
              <Phone size={15} /> <span className="text-[13px] tracking-wide">{company.phone}</span>
            </a>
            {isModern ? (
              <Link to="/offerte" className="btn-cta whitespace-nowrap text-[13px] px-5 py-2.5" data-testid="navbar-cta-offerte">
                Offerte Aanvragen
              </Link>
            ) : (
              <Link to="/offerte" className="btn-gold whitespace-nowrap text-[12px] px-5 py-2.5" data-testid="navbar-cta-offerte">
                Offerte Aanvragen
              </Link>
            )}
          </div>

          <button
            className={`lg:hidden ${isModern ? "text-m-text" : "text-white"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Desktop submenus */}
        {submenu === "diensten" && (
          <div
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            className={submenuPanelCls}
            data-testid="navbar-diensten-submenu"
          >
            <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 py-10 grid grid-cols-5 gap-8">
              {serviceCategories.map((c) => (
                <div key={c.slug}>
                  <Link to={`/diensten#${c.slug}`} className={`block ${isModern ? "text-m-petrol font-bold" : "font-display text-koning-gold"} text-base mb-4`}>
                    {c.title}
                  </Link>
                  <ul className="space-y-2.5">
                    {(servicesGrouped[c.slug] || []).map((s) => (
                      <li key={s.slug}>
                        <Link
                          to={`/diensten/${s.slug}`}
                          className={`text-sm transition-colors ${isModern ? "text-m-text-2 hover:text-m-orange" : "text-koning-muted hover:text-white"}`}
                          data-testid={`submenu-service-${s.slug}`}
                        >
                          {s.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {submenu === "locaties" && (
          <div
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            className={submenuPanelCls}
            data-testid="navbar-locaties-submenu"
          >
            <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 py-10 grid grid-cols-6 gap-6">
              {cities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/locaties/${c.slug}`}
                  className="group block"
                  data-testid={`submenu-city-${c.slug}`}
                >
                  <div className={`text-base font-semibold transition-colors ${isModern ? "text-m-text group-hover:text-m-orange" : "font-display text-white group-hover:text-koning-gold"}`}>{c.name}</div>
                  <div className={`text-xs mt-1 ${isModern ? "text-m-muted" : "text-koning-muted"}`}>Zuid-Holland</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className={`lg:hidden max-h-[calc(100vh-72px)] overflow-y-auto ${isModern ? "bg-white border-t border-m-border-soft" : "bg-koning-bg border-t border-koning-border"}`} data-testid="navbar-mobile-drawer">
          <div className="px-6 py-6 space-y-1">
            <MobileLink to="/" label="Home" isModern={isModern} />
            <MobileGroup label="Diensten" isModern={isModern}>
              {serviceCategories.map((c) => (
                <div key={c.slug} className="pl-4 mt-3">
                  <div className={`text-xs tracking-[0.22em] uppercase mb-2 ${isModern ? "text-m-petrol" : "text-koning-gold"}`}>{c.title}</div>
                  {(servicesGrouped[c.slug] || []).map((s) => (
                    <Link
                      key={s.slug}
                      to={`/diensten/${s.slug}`}
                      className={`block py-2 text-sm ${isModern ? "text-m-text-2 hover:text-m-orange" : "text-koning-muted hover:text-white"}`}
                      data-testid={`mobile-service-${s.slug}`}
                    >{s.title}</Link>
                  ))}
                </div>
              ))}
            </MobileGroup>
            <MobileGroup label="Locaties" isModern={isModern}>
              <div className="pl-4 mt-2 grid grid-cols-2 gap-2">
                {cities.map((c) => (
                  <Link key={c.slug} to={`/locaties/${c.slug}`} className={`py-2 text-sm ${isModern ? "text-m-text-2 hover:text-m-orange" : "text-koning-muted hover:text-white"}`}>{c.name}</Link>
                ))}
              </div>
            </MobileGroup>
            <MobileLink to="/projecten" label="Projecten" isModern={isModern} />
            <MobileLink to="/configurator" label="Configurator" isModern={isModern} />
            <MobileLink to="/over-ons" label="Over Ons" isModern={isModern} />
            <MobileLink to="/reviews" label="Reviews" isModern={isModern} />
            <MobileLink to="/faq" label="FAQ" isModern={isModern} />
            <MobileLink to="/contact" label="Contact" isModern={isModern} />
            <div className="pt-6">
              <Link to="/offerte" className={isModern ? "btn-cta w-full" : "btn-gold w-full"} data-testid="mobile-cta-offerte">Offerte Aanvragen</Link>
              <a href={company.phoneHref} onClick={() => trackClick("phone", "navbar-mobile")} className={`mt-4 block text-center text-sm ${isModern ? "text-m-text-2" : "text-koning-muted"}`}>{company.phone}</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const NavItem = ({ to, label, onHover, isModern }) => (
  <NavLink
    to={to}
    end={to === "/"}
    onMouseEnter={onHover}
    className={({ isActive }) => {
      const base = isModern
        ? "text-[14px] font-semibold whitespace-nowrap transition-colors"
        : "text-[12px] tracking-[0.16em] uppercase whitespace-nowrap transition-colors";
      const color = isModern
        ? (isActive ? "text-m-orange" : "text-m-text hover:text-m-orange")
        : (isActive ? "text-koning-gold" : "text-white hover:text-koning-gold");
      return `${base} ${color}`;
    }}
    data-testid={`navbar-link-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    {label}
  </NavLink>
);

const MobileLink = ({ to, label, isModern }) => (
  <NavLink
    to={to}
    end={to === "/"}
    className={({ isActive }) => {
      const c = isModern
        ? (isActive ? "text-m-orange" : "text-m-text")
        : (isActive ? "text-koning-gold" : "text-white");
      return `block py-3 text-base font-semibold ${c}`;
    }}
  >{label}</NavLink>
);

const MobileGroup = ({ label, isModern, children }) => {
  const [o, setO] = useState(false);
  return (
    <div className={`border-b ${isModern ? "border-m-border-soft" : "border-koning-border/50"}`}>
      <button onClick={() => setO((v) => !v)} className={`flex items-center justify-between w-full py-3 text-base font-semibold ${isModern ? "text-m-text" : "text-white"}`}>
        <span>{label}</span>
        <ChevronDown size={18} className={o ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {o && <div className="pb-3">{children}</div>}
    </div>
  );
};
